import { lazy } from "react";
import {
  AppShell,
  createTheme,
  localStorageColorSchemeManager,
  MantineProvider,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { Redirect, Route, Switch, Router } from "wouter";
import { ErrorBoundary } from "@sentry/react";
import { useTranslation } from "react-i18next";
import { FeatureErrorBoundary } from "./_Shared/FeatureErrorBoundary";

import Confirmation from "../../Boilerplate/Auth/Confirmation";
import ForgotPassword from "../../Boilerplate/Auth/ForgotPassword";
import LoginForm from "../../Boilerplate/Auth/Login";
import LoginLinkHandler from "../../Boilerplate/Auth/LoginLink";
import ResetPassword from "../../Boilerplate/Auth/ResetPassword";
import ProfileEdit from "../../Boilerplate/Profile/ProfileEdit";
import UserList from "../../Boilerplate/Users/UserList";
import UserAdd from "../../Boilerplate/Users/UserAdd";
import UserEdit from "../../Boilerplate/Users/UserEdit";
import TenantList from "../../Boilerplate/AdminTenants/TenantList";
import TenantEdit from "../../Boilerplate/AdminTenants/TenantEdit";
import TenantCreate from "../../Boilerplate/AdminTenants/TenantCreate";
import TenantUserCreate from "../../Boilerplate/Tenants/TenantUserCreate";
import TenantProfileEdit from "../../Boilerplate/Tenants/TenantProfileEdit";
import ContentSettingsCard from "./Settings/ContentSettings";
import AdminUsersList from "../../Boilerplate/AdminUsers/AdminUsersList";
import AdminUserDetails from "../../Boilerplate/AdminUsers/AdminUserDetails";
import JobDetailsAdmin from "../../Boilerplate/AdminJobs/JobDetails";
import JobListAdmin from "../../Boilerplate/AdminJobs/Jobs";
import AdminDashboard from "./Admin/AdminDashboard";
import ClassificationStandards from "./Admin/ClassificationStandards";
import SchemaPortabilityAdmin from "./Admin/SchemaPortabilityAdmin";
import ChangePassword from "../../Boilerplate/Auth/ChangePassword";
import RegisterForm from "../../Boilerplate/Auth/Register";
import UserDetails from "../../Boilerplate/Users/UserDetails";
import InvitationForm from "../../Boilerplate/Auth/Invitation";

import { NBoilerplateContext } from "../../Boilerplate/NBoilerplateContext";

import ErrorFallback from "../../Boilerplate/_Shared/ErrorFallback";
import Home from "./Home";
import ProductsList from "./Products/List";
import ProductDetails from "./Products/Details";

import { HeaderMenu } from "./HeaderMenu";
import AssistantPanel from "./Assistant/AssistantPanel";
import { useAssistantStore } from "./Assistant/useAssistant";
import AbstractMappingEdit from "./AbstractMapping/AbstractMappingEdit";
import PaymentSuccess from "./Payment/Success";
import PaymentCancelled from "./Payment/Cancelled";
import { CreditsPricingPage } from "./Payment/CreditsPricingPage";
import UploadPage from "./Imports/UploadPage";
import ImportList from "./Imports/ImportList";
import ImportDetails from "./Imports/ImportDetails";
import MappingDetails from "./Mappings/MappingDetails";
import MappingProfiles from "./Mappings/MappingProfiles";
import MappingProfileForm from "./Mappings/MappingProfileForm";
import MappingProfileDetail from "./Mappings/MappingProfileDetail";
import PricingPage from "./Pricing/PricingPage";
import { useDisclosure } from "@mantine/hooks";
import Navbar from "./NavBar";

import { store, useStore } from "./store";

const colorSchemeManager = localStorageColorSchemeManager({
  key: "facetflux-color-scheme",
});
const EsskaProductList = lazy(
  () => import("./Products/Esska/EsskaProductList.jsx"),
);
const EsskaProductDetails = lazy(
  () => import("./Products/Esska/EsskaProductDetails.jsx"),
);
const TranslationList = lazy(
  () => import("./Translations/TranslationList.jsx"),
);
const TranslationUsage = lazy(
  () => import("./Translations/TranslationUsage.jsx"),
);
const TranslationDetails = lazy(
  () => import("./Translations/TranslationDetails.jsx"),
);
const FreeTextTranslate = lazy(
  () => import("./Translations/FreeTextTranslate.tsx"),
);

const ChannelList = lazy(() => import("./Pim/Channels/ChannelList"));
const ChannelDetail = lazy(() => import("./Pim/Channels/ChannelDetail"));
const ChannelDashboard = lazy(() => import("./Pim/Channels/ChannelDashboard"));
const SchemaList = lazy(() => import("./Pim/Schemas/SchemaList"));
const SchemaEditor = lazy(() => import("./Pim/Schemas/SchemaEditor"));
const PimProductList = lazy(() => import("./Pim/Products/PimProductList"));
const PimProductCreate = lazy(() => import("./Pim/Products/PimProductCreate"));
const PimProductDetails = lazy(
  () => import("./Pim/Products/PimProductDetails"),
);
const ValidationDashboard = lazy(
  () => import("./Validation/ValidationDashboard"),
);
const SchemaDefinitionList = lazy(
  () => import("./Pim/SchemaDefinitions/SchemaDefinitionList"),
);
const SchemaDefinitionEditor = lazy(
  () => import("./Pim/SchemaDefinitions/SchemaDefinitionEditor"),
);
const OptiviseIntegration = lazy(
  () => import("./Settings/OptiviseIntegration"),
);
const StoreConnections = lazy(() => import("./Settings/StoreConnections"));
const DataQualityDashboard = lazy(
  () => import("./Pim/DataQuality/DataQualityDashboard"),
);
const ProductGrid = lazy(
  () => import("./Pim/Products/ProductGrid"),
);
const SmartImportWizard = lazy(
  () => import("./SmartImport/SmartImportWizard"),
);
const CatalogPage = lazy(
  () => import("./Pim/Catalog/CatalogPage"),
);
const CatalogDetail = lazy(
  () => import("./Pim/Catalog/CatalogDetail"),
);
const AcceptSchemaInvitation = lazy(
  () => import("./Vendor/AcceptInvitation"),
);
const SubmissionList = lazy(
  () => import("./Vendor/SubmissionList"),
);
const SubmissionCreate = lazy(
  () => import("./Vendor/SubmissionCreate"),
);
const SubmissionDetail = lazy(
  () => import("./Vendor/SubmissionDetail"),
);
const EsskaWawiUpload = lazy(
  () => import("./Pim/Imports/EsskaWawiUpload"),
);
const DiagnosePage = lazy(
  () => import("./Diagnostics/DiagnosePage"),
);

function App() {
  const { t } = useTranslation("common");
  const profile = useStore((state) => state.profile);
  const [opened, { toggle }] = useDisclosure(false);
  const assistantOpen = useAssistantStore((s) => s.isOpen);

  // Brand colors: Electric Violet (Primary Accent - The Flux)
  const facetflux = [
    "#f5ebff",
    "#e3d3ff",
    "#c3a3f9",
    "#a171f3",
    "#7c3aed", // Brand primary
    "#722bec",
    "#691deb",
    "#5811d2",
    "#4e0cbc",
    "#4206a6",
  ];

  // Cyan Ray (Secondary Accent - The Facet)
  const cyanray = [
    "#ecfeff",
    "#cffafe",
    "#a5f3fc",
    "#67e8f9",
    "#22d3ee", // Brand secondary
    "#06b6d4",
    "#0891b2",
    "#0e7490",
    "#155e75",
    "#164e63",
  ];

  const theme = createTheme({
    primaryColor: "facetflux",
    defaultRadius: "md",
    colors: {
      facetflux,
      cyanray,
    },
    fontFamily: "'Space Grotesk', system-ui, -apple-system, sans-serif",
    fontFamilyMonospace: "'JetBrains Mono', 'Courier New', monospace",
    headings: {
      fontFamily: "'Space Grotesk', system-ui, -apple-system, sans-serif",
      fontWeight: "600",
    },
    black: "#0D0D12", // Void Black
    other: {
      voidBlack: "#0D0D12",
      electricViolet: "#7C3AED",
      cyanRay: "#22D3EE",
      terminalGreen: "#10B981",
      neonCoral: "#F43F5E",
      faintSlate: "#334155",
    },
  });

  return (
    <Router>
      <NBoilerplateContext.Provider value={store}>
        <MantineProvider
          theme={theme}
          defaultColorScheme="dark"
          colorSchemeManager={colorSchemeManager}
        >
          <ModalsProvider>
            <Notifications />
            <AppShell
              header={{ height: 60 }}
              padding="xs"
              navbar={{
                width: 220,
                breakpoint: "lg",
                collapsed: { mobile: !opened, desktop: true },
              }}
              aside={{
                width: 380,
                breakpoint: "md",
                collapsed: { mobile: !assistantOpen, desktop: !assistantOpen },
              }}
            >
              <AppShell.Header>
                <ErrorBoundary fallback={ErrorFallback}>
                  <HeaderMenu opened={opened} toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Header>
              <AppShell.Navbar p={0}>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Navbar toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Navbar>
              {assistantOpen && (
                <AppShell.Aside>
                  <AssistantPanel />
                </AppShell.Aside>
              )}
              <AppShell.Main>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Switch>
                    <Route path="/home" component={Home} />
                    <Route path="/products" component={ProductsList} />
                    <Route path="/products/:id" component={ProductDetails} />

                    <Route path="/infer/tree/:id" component={SmartImportWizard} />
                    <Route path="/infer/:id" component={SmartImportWizard} />
                    <Route path="/infer" component={SmartImportWizard} />
                    <Route path="/imports/upload" component={UploadPage} />

                    <Route path="/imports" component={ImportList} />
                    <Route path="/imports/:id" component={ImportDetails} />
                    <Route
                      path="/imports/:importId/mappings/:mappingId/products"
                      component={ProductsList}
                    />
                    <Route
                      path="/imports/:importId/mappings/:mappingId"
                      component={MappingDetails}
                    />

                    {/* Mapping Profiles */}
                    <Route
                      path="/mapping-profiles"
                      component={MappingProfiles}
                    />
                    <Route
                      path="/mapping-profiles/new"
                      component={MappingProfileForm}
                    />
                    <Route path="/mapping-profiles/:id">
                      {(params) => (
                        <FeatureErrorBoundary featureName={t("error_fallback.features.mappingProfile")}>
                          <MappingProfileDetail params={params} />
                        </FeatureErrorBoundary>
                      )}
                    </Route>

                    <Route
                      path="/esska/products"
                      component={EsskaProductList}
                    />
                    <Route
                      path="/esska/products/:id"
                      component={EsskaProductDetails}
                    />

                    {/* PIM Routes */}
                    <Route path="/pim/schemas" component={SchemaList} />
                    <Route path="/pim/schemas/:id">
                      <FeatureErrorBoundary featureName={t("error_fallback.features.schemaEditor")}>
                        <SchemaEditor />
                      </FeatureErrorBoundary>
                    </Route>
                    <Route path="/pim/imports/esska-wawi" component={EsskaWawiUpload} />
                    <Route path="/pim/products">
                      <FeatureErrorBoundary featureName={t("error_fallback.features.productList")}>
                        <PimProductList />
                      </FeatureErrorBoundary>
                    </Route>
                    <Route
                      path="/pim/products/create"
                      component={PimProductCreate}
                    />
                    <Route path="/pim/products/grid" component={ProductGrid} />
                    <Route path="/pim/products/:id">
                      {(params) => (
                        <FeatureErrorBoundary featureName={t("error_fallback.features.productDetails")}>
                          <PimProductDetails params={params} />
                        </FeatureErrorBoundary>
                      )}
                    </Route>

                    {/* Channels */}
                    <Route path="/pim/channels" component={ChannelList} />
                    <Route path="/pim/channels/dashboard" component={ChannelDashboard} />
                    <Route path="/pim/channels/:id" component={ChannelDetail} />

                    {/* Catalog (browse view) */}
                    <Route path="/pim/catalog" component={CatalogPage} />
                    <Route path="/pim/catalog/:id" component={CatalogDetail} />

                    {/* Data Quality */}
                    <Route path="/pim/data-quality" component={DataQualityDashboard} />

                    {/* Free catalog diagnostic */}
                    <Route path="/diagnose" component={DiagnosePage} />

                    {/* Validation */}
                    <Route path="/validation" component={ValidationDashboard} />

                    {/* Schema Definitions (File Format Templates) */}
                    <Route path="/settings/schemas" component={SchemaDefinitionList} />
                    <Route path="/settings/schemas/:id" component={SchemaDefinitionEditor} />

                    {/* Integrations */}
                    <Route path="/settings/integrations/optivise" component={OptiviseIntegration} />
                    <Route path="/settings/integrations/stores" component={StoreConnections} />

                    {/* Vendor Portal */}
                    <Route path="/vendor/accept/:token" component={AcceptSchemaInvitation} />
                    <Route path="/vendor/submissions/create" component={SubmissionCreate} />
                    <Route path="/vendor/submissions/:id" component={SubmissionDetail} />
                    <Route path="/vendor/submissions" component={SubmissionList} />

                    {/* Boilerplate, User, Tenants etc. */}
                    <Route path={`/invite/:token`}>
                      {(params) => (
                        <InvitationForm appName="FacetFlux" params={params} />
                      )}
                    </Route>

                    <Route path={`/auth/reset`}>
                      <ForgotPassword realm="FFUser" />
                    </Route>
                    <Route
                      path={`/auth/reset-password/:token`}
                      component={ResetPassword}
                    />
                    <Route path={`/auth/login`}>
                      <LoginForm realm="FFUser" />
                    </Route>
                    <Route
                      path={`/auth/login/:token`}
                      component={LoginLinkHandler}
                    />
                    <Route
                      path={`/auth/confirm-email/:token`}
                      component={Confirmation}
                    />

                    <Route
                      path={`/auth/change-password`}
                      component={ChangePassword}
                    />
                    <Route path={`/auth/register`}>
                      <RegisterForm
                        realm="FFUser"
                        touLink="https://www.facetflux.com/legal"
                      />
                    </Route>

                    <Route path={`/profile`} component={ProfileEdit} />
                    <Route path={`/plans`} component={PricingPage} />
                    <Route path={`/credits`} component={CreditsPricingPage} />
                    <Route
                      path="/payment/completed"
                      component={PaymentSuccess}
                    />
                    <Route
                      path="/payment/cancelled"
                      component={PaymentCancelled}
                    />

                    <Route
                      path="/translations/new"
                      component={FreeTextTranslate}
                    />
                    <Route path="/translations/requests" component={TranslationList} />
                    <Route path="/translations" component={TranslationUsage} />
                    <Route
                      path="/translations/:id"
                      component={TranslationDetails}
                    />

                    {/* TENANT OWNER STUFF */}
                    {(profile?.role == "TenantOwner" ||
                      profile?.role == "TenantAdmin") && (
                      <>
                        <Route path={`/settings/tenant`}>
                          <TenantProfileEdit showSubscription={true} />
                          <ContentSettingsCard />
                        </Route>

                        <Route path={`/users`} component={UserList} />
                        <Route path={`/users/add`}>
                          <UserAdd realm="FFUser" />
                        </Route>
                        <Route
                          path={`/users/details/:id`}
                          component={UserDetails}
                        />
                        <Route path={`/users/edit/:id`} component={UserEdit} />
                      </>
                    )}

                    {/* ADMIN STUFF */}
                    {profile?.role == "GlobalAdmin" && (
                      <>
                        <Route path="/admin/jobs" component={JobListAdmin} />
                        <Route
                          path="/admin/jobs/:id"
                          component={JobDetailsAdmin}
                        />

                        {/* <Route
                          path="/marketplaces"
                          component={MarketplaceList}
                        />
                        <Route
                          path="/marketplaces/create"
                          component={MarketplaceEdit}
                        />
                        <Route
                          path="/marketplaces/:id"
                          component={MarketplaceEdit}
                        /> */}

                        <Route
                          path={`/marketplaces/:marketplaceId/mapping`}
                          component={AbstractMappingEdit}
                        />

                        <Route path={`/tenants`} component={TenantList} />
                        <Route
                          path={`/tenants/create`}
                          component={TenantCreate}
                        />
                        <Route path={`/tenants/:id`} component={TenantEdit} />

                        <Route
                          path={`/tenants/:tenantId/users/create`}
                          component={TenantUserCreate}
                        />
                        <Route
                          path="/admin/standards"
                          component={ClassificationStandards}
                        />
                        <Route
                          path="/admin/schema-portability"
                          component={SchemaPortabilityAdmin}
                        />
                        <Route
                          path={`/admin/users`}
                          component={AdminUsersList}
                        />
                        <Route
                          path={`/admin/users/:userId`}
                          component={AdminUserDetails}
                        />
                        <Route path="/admin" component={AdminDashboard} />
                      </>
                    )}

                    <Route
                      path="/"
                      component={() => {
                        // console.log("profile", profile);
                        if (profile?.role) {
                          return <Redirect to="/home" />;
                        } else {
                          return <Redirect to="/auth/login" />;
                        }
                      }}
                    />
                    <Route>{t("generic.notFound")}</Route>
                  </Switch>
                </ErrorBoundary>
              </AppShell.Main>
            </AppShell>
          </ModalsProvider>
        </MantineProvider>
      </NBoilerplateContext.Provider>
    </Router>
  );
}

export default App;
